import {getPageComponentFromComponentMap} from '@packages/utilities/platform';
import {isDynamoWrapper} from '@packages/utilities/elementTypeUtils';
import {
  isCollectionConnectedDynamoWrapperElement,
  isMultiReferenceFieldConnectedDynamoWrapperElement,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';

import type {ConsolidatedBindingContext} from '../consolidateBindingContext';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import type {DynamicContentFlags, DynamicContext} from './types';

const DYNAMIC_ELEMENT_MOVE_ERROR =
  'Dynamic elements can only be moved within the same Collection List or Collection Page.';

const ELEMENT_WITH_DYN_CONTEXT_CONDITIONS_MOVE_ERROR =
  'Elements with conditional visibility using “Current Item“ can only be moved within the same Collection.';

const DYNAMIC_ELEMENT_TO_COMPONENT_MOVE_ERROR =
  'Components can only contain dynamic elements that are inside a Collection List Item.';

const MULTI_REFERENCE_LIST_MOVE_ERROR =
  'Multi-Reference Collection lists can only be moved within the same Collection.';

const COLLECTION_LIST_MOVE_ERROR =
  'Collection Lists connected to CMS collections cannot be moved inside other Collection Lists.';

const COLLECTION_LIST_WITH_DYN_CONTEXT_FILTERS_MOVE_ERROR =
  'Collection Lists with filters using “Current Item“ can only be moved within the same Collection Page.';

const COLLECTION_LIST_WITH_DYN_CONTEXT_CONDITIONS_MOVE_ERROR =
  'Collection Lists that contain elements with conditional visibility using “Current Item“ can only be moved within the same Collection Page.';

export function getGenericError() {
  return DYNAMIC_ELEMENT_MOVE_ERROR;
}

export type ErrorCode =
  | 'Dynamic Element'
  | 'Element With DYN_CONTEXT Conditions'
  | 'Dynamic Element To Component'
  | 'Multi-Reference Collection List'
  | 'Collection List'
  | 'Collection List With DYN_CONTEXT Filters'
  | 'Collection List With DYN_CONTEXT Conditions';

export function parseErrorMessageToErrorCode(errorMessage: string): ErrorCode {
  switch (errorMessage) {
    case ELEMENT_WITH_DYN_CONTEXT_CONDITIONS_MOVE_ERROR: {
      return 'Element With DYN_CONTEXT Conditions';
    }
    case DYNAMIC_ELEMENT_TO_COMPONENT_MOVE_ERROR: {
      return 'Dynamic Element To Component';
    }
    case MULTI_REFERENCE_LIST_MOVE_ERROR: {
      return 'Multi-Reference Collection List';
    }
    case COLLECTION_LIST_MOVE_ERROR: {
      return 'Collection List';
    }
    case COLLECTION_LIST_WITH_DYN_CONTEXT_FILTERS_MOVE_ERROR: {
      return 'Collection List With DYN_CONTEXT Filters';
    }
    case COLLECTION_LIST_WITH_DYN_CONTEXT_CONDITIONS_MOVE_ERROR: {
      return 'Collection List With DYN_CONTEXT Conditions';
    }
    case DYNAMIC_ELEMENT_MOVE_ERROR:
    default: {
      return 'Dynamic Element';
    }
  }
}

export function getBoundaryCreatorError(element: ExpressionElement): string {
  const isDynamoWrapperElement = isDynamoWrapper(element);

  if (
    isDynamoWrapperElement &&
    isCollectionConnectedDynamoWrapperElement(element)
  ) {
    return COLLECTION_LIST_MOVE_ERROR;
  }

  if (
    isDynamoWrapperElement &&
    isMultiReferenceFieldConnectedDynamoWrapperElement(element)
  ) {
    return MULTI_REFERENCE_LIST_MOVE_ERROR;
  }

  return DYNAMIC_ELEMENT_MOVE_ERROR;
}

export function getMismatchedBindingContextsError(
  flags: DynamicContentFlags,
  targetBindingContext: ConsolidatedBindingContext | null,
  target: DynamicContext
): string {
  switch (getHighestPriorityDynamicContentFlag(flags)) {
    case 'collectionListsWithContextualFilters': {
      return COLLECTION_LIST_WITH_DYN_CONTEXT_FILTERS_MOVE_ERROR;
    }
    case 'collectionListsWithContextualConditions': {
      return COLLECTION_LIST_WITH_DYN_CONTEXT_CONDITIONS_MOVE_ERROR;
    }
    case 'orphanBindingsOrConditions': {
      return isInsideSymbolRootContext(targetBindingContext, target)
        ? DYNAMIC_ELEMENT_TO_COMPONENT_MOVE_ERROR
        : DYNAMIC_ELEMENT_MOVE_ERROR;
    }
    default: {
      return DYNAMIC_ELEMENT_MOVE_ERROR;
    }
  }
}

function getHighestPriorityDynamicContentFlag(
  flags: DynamicContentFlags
): keyof DynamicContentFlags {
  switch (true) {
    case flags.orphanContextualFilterProps:
    case flags.collectionListsWithContextualFilters: {
      return 'collectionListsWithContextualFilters';
    }
    case flags.collectionListsWithContextualConditions: {
      return 'collectionListsWithContextualConditions';
    }
    case flags.orphanBindingsOrConditions:
    default: {
      return 'orphanBindingsOrConditions';
    }
  }
}

function isInsideSymbolRootContext(
  targetBindingContext: ConsolidatedBindingContext | null,
  target: DynamicContext
) {
  return (
    !targetBindingContext &&
    target.component !== getPageComponentFromComponentMap(target.componentMap)
  );
}

export function getMismatchedParentBindingContextsError() {
  return ELEMENT_WITH_DYN_CONTEXT_CONDITIONS_MOVE_ERROR;
}
